export const edgesToNodes = (resource, type) => {
  if (!resource || !type) return [];
  const nodeResourceSources = !resource.edges ? resource : resource.edges;
  return nodeResourceSources.map((nodeResource) => {
    const node = !nodeResource.node ? nodeResource : nodeResource.node;
    switch (type) {
      case 'cartline':
        return formatCartLine(node);
      case 'image':
        return formatImage(node);
      case 'product':
        return formatProduct(node);
      case 'variant':
        return formatVariant(node);
      case 'metaObject':
        return formatMetaObject(node);
      default:
        return null;
    }
  });
};

export const formatBundle = (prods) => {
  const products = edgesToNodes(prods, 'product');
  const activeVariants = products.map((p) =>
    p.variants.find((v) => v.availableForSale),
  );
  const bundleValue = products.reduce((total, product) => {
    const totalPrice =
      total + parseFloat(product.priceRange.minVariantPrice.amount);
    return totalPrice;
  }, 0);

  return {
    products,
    activeVariants: products.map((p) =>
      p.variants.find((v) => v.availableForSale),
    ),
    available: activeVariants.every((v) => v.availableForSale),
    value: {
      amount: bundleValue,
      currencyCode: window.TMW.localization.currencyCode,
    },
  };
};

export const formatCart = (cart) => {
  const subtotal = cart?.cost?.subtotalAmount || {
    amount: 0,
    currencyCode: window.TMW.localization.currencyCode,
  };
  return {
    ...cart,
    subtotal,
    lines: formatCartLines(edgesToNodes(cart?.lines, 'cartline')),
  };
};

export const formatCartLine = (line) => ({
  ...line,
});

export const formatCartLines = (lines) => {
  if (!lines) return [];

  const processedBundles = [];
  const formattedLines = lines
    .map((line) => {
      const bundleProductsId = line.attributes?.find(
        (attr) => attr.key === '_bundleProductsId',
      );
      // If not a bundle item return the default line object
      if (!bundleProductsId) return formatCartLine(line);
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId.value)) return null;

      // Get bundle items
      const bundleItems = lines
        .filter((l) => {
          const currentId = l.attributes.find(
            (attr) => attr.key === '_bundleProductsId',
          )?.value;
          if (currentId === bundleProductsId.value) return l;
          return null;
        })
        .map((l) => l);

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId.value);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(
        firstItem.attributes.find((a) => a.key === '_bundleData').value,
      );

      // Return bundle line item
      return {
        merchandise: bundleData.merchandise,
        cost: bundleData.cost,
        quantity: firstItem.quantity,
        children: bundleItems,
      };
    }, [])
    .filter((i) => i);

  return formattedLines.filter((x) => x);
};

export const formatCartLinesAjax = (lines) => {
  if (!lines) return [];

  const processedBundles = [];
  const formattedLines = lines
    .map((line) => {
      const bundleProductsId = line.properties?._bundleProductsId;
      // If not a bundle item return the default line object
      if (!bundleProductsId) return line;
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId)) return null;

      // Get bundle items
      const bundleItems = lines
        .filter((l) => {
          const currentId = l.properties?._bundleProductsId;
          if (currentId === bundleProductsId) return l;
          return null;
        })
        .map((l) => l);

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(firstItem.properties._bundleData);

      // Return bundle line item
      return {
        merchandise: bundleData.merchandise,
        cost: bundleData.cost,
        quantity: firstItem.quantity,
        children: bundleItems,
      };
    }, [])
    .filter((i) => i);

  return formattedLines.filter((x) => x);
};

export const formatImage = (image) => ({
  ...image,
});

export const formatProduct = (product) => ({
  ...product,
  variants: edgesToNodes(product.variants, 'variant'),
  images: edgesToNodes(product.images, 'image'),
  media: edgesToNodes(product.media, 'image'),
  collectionsId: product.collections.edges.map((i) => i.node.id),
  sizeGuide: product?.sizeGuide?.value ? product.sizeGuide.value : null,
  groupId:
    product.tags.find((tag) => tag.toLowerCase().includes('csku::')) || null,
  benefits: edgesToNodes(product.benefits?.references, 'metaObject'),
  hasOnlyDefaultVariant:
    product.options.length === 1 &&
    product.options[0].values[0] === 'Default Title',
});

export const formatVariant = (variant) => ({
  ...variant,
});

export const formatMetaObject = (metaObject) =>
  Object.fromEntries(metaObject.fields.map((item) => [item.key, item]));

export const formatMoney = (
  { amount, currencyCode = Shopify.currency?.active || 'USD' },
  cents = false,
) => {
  let amt = amount;
  if (Number.isNaN(amt))
    // eslint-disable-next-line no-console
    return console.error('utils/format-money.js: No amount value passed.');

  if (cents) amt /= 100;

  const formatter = new Intl.NumberFormat(
    `${Shopify.locale}-${Shopify.country}`,
    {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currencyDisplay: 'symbol',
    },
  );

  return formatter.format(amt).replace('.00', '').replace(',00', '');
};

export const formatRichTextJson = (content) => {
  const parsed =
    typeof content === 'object' && content !== null
      ? content
      : JSON.parse(content);

  let html = '';

  parsed.children.forEach((node) => {
    switch (node.type) {
      case 'heading':
        html += `<h${node.level}>${node.children[0].value}</h${node.level}>`;
        break;
      case 'list':
        html += `<${node.listType === 'unordered' ? 'ul' : 'ol'}>`;
        node.children.forEach((item) => {
          html += `<li>${item.children[0].value}</li>`;
        });
        html += `<${node.listType === 'unordered' ? '/ul' : '/ol'}>`;
        break;
      case 'paragraph':
        html += `<p>`;
        node.children.forEach((item) => {
          if (item.type === 'text' && item.bold) {
            html += `<strong>${item.value}</strong> `;
          } else if (item.type === 'text' && item.italic) {
            html += `<em>${item.value}</em> `;
          } else if (item.type === 'text') {
            html += `${item.value} `;
          }
          if (item.type === 'link' && item.bold) {
            html += `<a href="${item.url}" target="${item.target}">
                    <strong>${item.children[0].value}</strong></a>`;
          } else if (item.type === 'link' && item.italic) {
            html += `<a href="${item.url}" target="${item.target}">
                    <em>${item.children[0].value}</em></a>`;
          } else if (item.type === 'link') {
            html += `<a href="${item.url}" target="${item.target}">
                   ${item.children[0].value}</a> `;
          }
        });
        html += `</p>`;
        break;
      default:
        html += '';
        break;
    }
  });
  return html;
};

export const formatLocations = (locations) => {
  const result = {};

  Object.keys(locations).forEach((key) => {
    const item = locations[key];
    if (item.hours) {
      result[item.storeId] = item;
    }
  });
  return result;
};
